import { Certification } from './types';

const externalDocument = (name: string, url: string) => ({
  name, url, storagePath: `external:${url}`, contentType: 'text/html', sizeBytes: 0,
});

export const ONBOARDING_CERTIFICATION_ID = 'overture-onboarding';
export const TEACHER_CERTIFICATION_ID = 'overture-teacher';

export const onboardingCertification: Certification = {
  id: ONBOARDING_CERTIFICATION_ID,
  title: 'Overture Onboarding',
  description: 'Complete your employment, account, compliance, and payroll setup.',
  kind: 'onboarding',
  order: 0,
  published: true,
  steps: [
    {
      id: 'offer',
      certificationId: ONBOARDING_CERTIFICATION_ID,
      onboardingTaskId: 'offer',
      title: 'Review and accept your offer',
      description: 'Review your offer letter and accept it to begin onboarding.',
      completionType: 'acknowledgment',
      required: true,
      order: 1,
      published: true,
    },
    {
      id: 'contract',
      certificationId: ONBOARDING_CERTIFICATION_ID,
      onboardingTaskId: 'contract',
      title: 'Employment contract',
      description: 'Review and sign your employment agreement.',
      completionType: 'document',
      required: true,
      order: 2,
      published: true,
    },
    {
      id: 'slack',
      certificationId: ONBOARDING_CERTIFICATION_ID,
      onboardingTaskId: 'slack',
      title: 'Communication (Slack)',
      description: 'Look for a Slack invitation email and set up your account.',
      completionType: 'acknowledgment',
      required: true,
      order: 3,
      published: true,
    },
    {
      id: 'profile',
      certificationId: ONBOARDING_CERTIFICATION_ID,
      onboardingTaskId: 'profile',
      title: 'Overture web-app profile',
      description: 'Complete your profile setup and availability.',
      completionType: 'acknowledgment',
      required: true,
      order: 4,
      published: true,
    },
    {
      id: 'handbook',
      certificationId: ONBOARDING_CERTIFICATION_ID,
      onboardingTaskId: 'handbook',
      title: 'Staff handbook',
      description: 'Read the Overture Staff Manual, then sign that you have read and will follow it.',
      completionType: 'document',
      linkUrl: '/docs/Overture_Teacher_Handbook_2026_v2.pdf',
      linkLabel: 'Staff handbook PDF',
      required: true,
      order: 5,
      published: true,
    },
    {
      id: 'backgroundCheck',
      certificationId: ONBOARDING_CERTIFICATION_ID,
      onboardingTaskId: 'backgroundCheck',
      title: 'Checkr background check',
      description: 'Complete the Checkr background check from your email invitation, then request a review so Overture can mark this step complete.',
      completionType: 'admin_approval',
      required: true,
      order: 6,
      published: true,
    },
    {
      id: 'schoolBackgroundCheck',
      certificationId: ONBOARDING_CERTIFICATION_ID,
      onboardingTaskId: 'schoolBackgroundCheck',
      title: 'Massachusetts CORI authorization form',
      description: 'Provide the information and authorization Overture needs to run your Massachusetts CORI check.',
      completionType: 'form_submission',
      required: false,
      order: 7,
      published: true,
    },
    {
      id: 'mandatedReporter',
      certificationId: ONBOARDING_CERTIFICATION_ID,
      onboardingTaskId: 'mandatedReporter',
      title: 'Mandated reporter compliance',
      description: 'Complete the required regional course and upload your certificate.',
      completionType: 'file_upload',
      required: true,
      order: 8,
      published: true,
    },
    {
      id: 'payroll',
      certificationId: ONBOARDING_CERTIFICATION_ID,
      onboardingTaskId: 'payroll',
      title: 'Payroll (Gusto)',
      description: 'Use your Gusto invitation to set up payroll.',
      completionType: 'acknowledgment',
      required: true,
      order: 9,
      published: true,
    },
    {
      id: 'merch',
      certificationId: ONBOARDING_CERTIFICATION_ID,
      onboardingTaskId: 'merch',
      title: 'Overture gear',
      description: 'Provide your shirt size.',
      completionType: 'acknowledgment',
      required: true,
      order: 10,
      published: true,
    },
  ],
};

export const teacherCertification: Certification = {
  id: 'overture-teacher',
  title: 'Overture Teaching Certification',
  description: 'Build the classroom skills and confidence to teach independently.',
  kind: 'standard',
  order: 1,
  published: true,
  steps: [
    ['childSafety', 'Child safety practice', 'Learn how Overture’s child-safety policies apply while leading a class.'],
    ['behaviorSop', 'Behavior response', 'Practice Overture’s process for responding to classroom behavior.'],
    ['materials', 'Materials and equipment', 'Learn how to prepare, use, and account for the materials required for a program.'],
    ['ogTraining', 'Live Q&A session', 'Attend a biweekly Overture Monday Get Together or schedule a live call with Steven or Aspen to get your questions answered before your first class.'],
  ].map(([id, title, description], index) => ({
    id, certificationId: 'overture-teacher', title, description,
    completionType: 'acknowledgment',
    ...(id === 'ogTraining' ? {
      linkUrl: 'https://meet.google.com/prm-igmd-ctj',
      linkLabel: 'Join the Monday Get Together',
      writtenContent: 'Overture Monday Get Together 🎉 (Optional)\nMonday, September 28 · 7:00–8:00pm\nTime zone: America/New_York\n\nThese sessions are held biweekly. Attend one session or schedule a live call with Steven or Aspen before your first class.\n\nDial-in: (US) +1 470-443-0028 · PIN: 727 592 552#',
    } : {}),
    documents: id === 'childSafety'
      ? [externalDocument('Review child safety policies', 'https://docs.google.com/document/u/1/d/1Ut-NaKHOND-daam3B6X3E7juWSYbFvxL/edit?usp=drive_link&ouid=107845449274425450840&rtpof=true&sd=true')]
      : id === 'behaviorSop'
        ? [externalDocument('Review behavior response SOP', 'https://docs.google.com/document/d/1UQ-X-D_vg-BrdHg4akuqkNiRr5OovV8Jan0txbSpFcM/edit?tab=t.0#heading=h.pp2lgiv2pgnm')]
        : id === 'materials'
          ? [externalDocument('Review materials and equipment', 'https://docs.google.com/document/d/1viIFAlKmeTvOhW41XCsaaymHOr_rSaeETuANOhn-xdg/edit?tab=t.0')]
          : id === 'ogTraining'
            ? [externalDocument('More phone numbers', 'https://tel.meet/prm-igmd-ctj?pin=4759996303504')]
          : [],
    order: index + 1, published: true,
  } as any)),
};

export const fallbackCertifications: Certification[] = [
  onboardingCertification,
  teacherCertification,
];

export const fallbackCertificationById = (id: string) =>
  fallbackCertifications.find(certification => certification.id === id) || null;
